/** Session state for the exec Custom Metrics panel (CONF-212, CONF-258).
 *  Overrides never enter `config`; they are lost on refresh unless the quote
 *  is saved (persistCustomMetrics). Far simpler than useExecPricing on
 *  purpose: no catalog fetch, no snapshot, no asOf. The exec gate is purely
 *  UI (button + panel) — this hook runs for every user so a loaded quote's
 *  saved overrides display for non-execs too (spec §5).
 *
 *  CONF-258 moved the override target from system throughput totals to each
 *  node's raw throughput rating (per-node keys, canonical GB/s — see
 *  domain/MetricsOverrides.ts). A quote saved under the old model (system
 *  totals, plus the now-retired capacity keys) is partitioned on `hydrate`:
 *  current-shaped entries replace the overrides immediately, while old-shaped
 *  throughput entries are held as `pending` until the live ThroughputBasis is
 *  known, then converted against it (domain/legacyMetricOverrides.ts). The
 *  basis present AT hydrate time still belongs to the PREVIOUS config —
 *  `loadSavedConfig` calls `loadConfig` and `hydrate` together, but metrics
 *  (and the basis) only recompute ~300ms later on the debounced effect — so
 *  conversion runs against the FIRST basis that differs from the one captured
 *  at hydrate, never that one itself. */
import { useState, useEffect, useCallback, useRef } from 'react';
import {
  emptyMetricOverrides, countMetricChanges, withMetricOverride, clearMetricOverride,
  clearMetricSet, toCanonical, metricMeta,
} from '../domain/MetricsOverrides';
import { partitionSavedOverrides, convertLegacyMetricOverrides, legacyNoticeMessage } from '../domain/legacyMetricOverrides';
import { devError } from '../utils/devLog';

export function useExecMetrics({ enabledSets, throughputBasis, onLegacyConverted }) {
  const [overrides, setOverrides] = useState(emptyMetricOverrides);
  // A hydrated legacy quote's not-yet-converted entries, plus the basis object
  // seen at hydrate time (so the conversion effect below can tell it apart
  // from a genuinely new basis). `null` when nothing is pending.
  const [pending, setPending] = useState(null);

  // Ref so an inline arrow passed by the caller doesn't retrigger the
  // conversion effect on every render.
  const onLegacyConvertedRef = useRef(onLegacyConverted);
  useEffect(() => { onLegacyConvertedRef.current = onLegacyConverted; }, [onLegacyConverted]);

  // Ref so `hydrate`'s identity stays stable (matching every other setter
  // here) while still reading the LATEST basis at call time. ConfigProvider
  // wraps this hook's whole return in a useMemo keyed only on `overrides` (its
  // callbacks were previously all stable); a `hydrate` whose own identity
  // depended on `throughputBasis` could be served stale — bound to a
  // throughputBasis from several recomputes back — to a consumer that reads
  // `execMetrics.hydrate` through that memo.
  //
  // Assigned DURING RENDER, not in a passive useEffect: the debounced
  // recompute's setCalculations fires from a setTimeout, not a React event,
  // so nothing forces its commit's passive effects to flush before some other
  // async callback (e.g. a quote fetch's .then()) runs loadSavedConfig and
  // calls hydrate. React only guarantees a ref written in render reflects the
  // value as of the most recent render — a passive effect can still be
  // sitting in the scheduler's queue when hydrate is called, so it would read
  // a basis up to one recompute stale. Writing the ref in the render body
  // itself has no such gap; this is safe specifically because the ref is
  // never read to produce THIS render's own output, only later from an
  // event-driven callback.
  const throughputBasisRef = useRef(throughputBasis);
  throughputBasisRef.current = throughputBasis;

  // Review Focus 3: a set the exec disabled must not keep invisible overrides.
  useEffect(() => {
    if (!enabledSets.afa) setOverrides((o) => clearMetricSet(o, 'afa'));
  }, [enabledSets.afa]);
  useEffect(() => {
    if (!enabledSets.hybrid) setOverrides((o) => clearMetricSet(o, 'hybrid'));
  }, [enabledSets.hybrid]);

  const setMetric = useCallback((key, displayValue, displayBaseline, isPBUnit) => {
    metricMeta(key);                                       // throws on an unknown key
    const value = toCanonical(displayValue, isPBUnit);
    const baseline = toCanonical(displayBaseline, isPBUnit);
    setOverrides((o) => withMetricOverride(o, key, value, baseline));
  }, []);

  const clearMetric = useCallback((key) => setOverrides((o) => clearMetricOverride(o, key)), []);
  const resetAll = useCallback(() => {
    setOverrides(emptyMetricOverrides());
    setPending(null);
  }, []);

  // A saved override set comes from quote storage, which this hook must not
  // trust blindly — an unknown key or a non-finite number would later throw
  // inside metricMeta() (useDisplayMetrics / metricDeltas) for every user
  // viewing that quote. partitionSavedOverrides sorts current-shaped entries
  // (kept), legacy-shaped throughput/capacity entries (held as `pending` for
  // conversion once the live basis is known) and anything else (dropped,
  // logged).
  const hydrate = useCallback((saved) => {
    const { current, legacy, invalid } = partitionSavedOverrides(saved);
    if (invalid.length) devError('useExecMetrics.hydrate: dropped invalid override keys', invalid);
    setOverrides(current);
    setPending(Object.keys(legacy).length > 0 ? { legacy, basis: throughputBasisRef.current } : null);
  }, []);

  // Converts pending legacy entries against the FIRST basis seen after
  // hydrate — never the one captured at hydrate time (the load-race guard
  // described above). A key missing from that basis (its set got disabled, or
  // the vendor switched, while the legacy entry was still pending) is dropped
  // rather than converted; a pure-capacity legacy quote drops everything.
  useEffect(() => {
    if (!pending || !throughputBasis || throughputBasis === pending.basis) return;
    const result = convertLegacyMetricOverrides(pending.legacy, throughputBasis);
    setOverrides((o) => ({ ...o, ...result.overrides }));
    setPending(null);
    const message = legacyNoticeMessage(result);
    if (message) onLegacyConvertedRef.current?.(message);
  }, [throughputBasis, pending]);

  return { overrides, changeCount: countMetricChanges(overrides), setMetric, clearMetric, resetAll, hydrate };
}
